/**
 * Shared task analytics source/actionType normalization.
 *
 * The DB stores `details.source` as a free-form string ("ai", "spine",
 * "user-created-inline", "levai_tool", …) and `actionType` as a wider string
 * than the canonical analytics enum. Both client (`trackTaskOpened`) and
 * server (`captureTask*`) emit `task_*` events; folding raw values into a
 * fixed enum here keeps dashboard breakdowns consistent across surfaces.
 * Pure module — safe to import from both server and client code.
 */

/** "unknown" is intentional: surfaces unclassifiable rows instead of dropping them. */
export type TaskAnalyticsSource =
  | "ai_generated"
  | "user"
  | "levai_tool"
  | "mcp_tool"
  | "unknown";

/**
 * Which task-generation model produced the task. Carried on the Tasks Usage
 * dashboard's lifecycle events (`task_classified`, `action_completed`) so the
 * v3 ladder/grade cohort and the v4 stage-as-rung/evidence cohort can be split
 * — the two models fire from different code paths with different provenance
 * (v4 Actions carry a `taskSpecId` and no `ladderId`, so `source` alone can't
 * tell them apart). "eval" is the task-eval-sync cohort: improvement tasks the
 * eval reconcile creates hidden (`src/lib/tasks/evalTaskAnalytics.ts` says
 * when they count as Generated and Added). The
 * capture helpers default to "v3" when omitted, so only the v4 and eval paths
 * opt in.
 */
export type TaskModel = "v3" | "v4" | "eval";

/**
 * Eval tasks persist `details.source: "user-added"` (the UI and queries branch
 * on it), so the source alone reads as founder-created. `evalOrigin` is the
 * only marker of Lev's generation: `actions.addUserImprovement` writes the
 * same source without it, and those rows are genuinely the founder's.
 */
export function isEvalOriginTask(details: unknown): boolean {
  if (!details || typeof details !== "object") return false;
  return (details as { evalOrigin?: unknown }).evalOrigin === true;
}

/**
 * Every emitter of one task's `action_completed` must agree on this: they share
 * a `$insert_id`, so whichever lands first fixes the model for good.
 */
export function resolveTaskModel(action: {
  taskSpecId?: string | null;
  details?: unknown;
}): TaskModel {
  if (action.taskSpecId) return "v4";
  if (isEvalOriginTask(action.details)) return "eval";
  return "v3";
}

/** "unknown" covers DB rows with an actionType outside the documented vocabulary. */
export type TaskAnalyticsActionType =
  | "discovery"
  | "decision"
  | "creation"
  | "evaluation"
  | "unknown";

/**
 * Evaluation tasks don't carry `details.source` — they're identified by
 * `actionType === "evaluation"`, which always implies AI-generated.
 */
export function normalizeTaskAnalyticsSource(
  rawSource: unknown,
  actionType?: string | null,
): TaskAnalyticsSource {
  if (typeof rawSource === "string") {
    if (rawSource === "levai_tool") return "levai_tool";
    if (rawSource === "mcp_tool") return "mcp_tool";
    if (rawSource.startsWith("user-")) return "user";
    if (rawSource === "ai" || rawSource === "spine" || rawSource === "ai_generated") {
      return "ai_generated";
    }
  }
  if (actionType === "evaluation") return "ai_generated";
  return "unknown";
}

export function normalizeTaskAnalyticsActionType(
  rawActionType: string | null | undefined,
): TaskAnalyticsActionType {
  switch (rawActionType) {
    case "discovery":
    case "decision":
    case "creation":
    case "evaluation":
      return rawActionType;
    default:
      return "unknown";
  }
}

export function readTaskAnalyticsSource(task: unknown): string | null {
  if (!task || typeof task !== "object") return null;
  const details = (task as { details?: unknown }).details;
  if (!details || typeof details !== "object") return null;
  const source = (details as { source?: unknown }).source;
  return typeof source === "string" ? source : null;
}

/**
 * `source` and `task_model` for a client-side task event, read off a cached
 * task row. An eval task stores the founder's "user-added" source, so it is
 * reported as Lev's (`resolveTaskGenerationSource` agrees). A row without the
 * `taskSpecId` column cannot tell v4 from v3, so its model is null, never v3.
 */
export function readTaskEventProvenance(task: unknown): {
  source: string | null;
  task_model: TaskModel | null;
} {
  const record =
    task && typeof task === "object"
      ? (task as { details?: unknown; taskSpecId?: unknown })
      : {};
  if (isEvalOriginTask(record.details)) return { source: "ai_generated", task_model: "eval" };
  const taskSpecId = record.taskSpecId;
  return {
    source: readTaskAnalyticsSource(task),
    task_model:
      typeof taskSpecId === "string" || taskSpecId === null
        ? resolveTaskModel({ taskSpecId, details: record.details })
        : null,
  };
}

/**
 * Resolve an Action's *generation* provenance for the lifecycle events
 * (`action_completed` / `action_deferred` / `action_deleted`) — i.e. who
 * created the task, NOT who acted on it. Used to split completion-rate
 * dashboards by user- vs Lev-generated work.
 *
 * `normalizeTaskAnalyticsSource` alone returns "unknown" for the dominant AI
 * path: v3 skeleton tasks persist `details: {}` with a placeholder
 * `actionType: "discovery"`. Those rows always carry a `ladderId` (generator
 * output — skeleton, v2 ladder, iteration, eval), whereas user-created tasks
 * have `ladderId: null` and a `user-*` `details.source`. So we fall back to
 * `ladderId` presence to classify unlabelled generator rows as AI-generated.
 */
export function resolveTaskGenerationSource(action: {
  details?: unknown;
  actionType?: string | null;
  ladderId?: string | null;
}): TaskAnalyticsSource {
  // Before normalization: an eval task's stored "user-added" would map to "user".
  if (isEvalOriginTask(action.details)) return "ai_generated";
  const normalized = normalizeTaskAnalyticsSource(
    readTaskAnalyticsSource(action),
    action.actionType,
  );
  if (normalized !== "unknown") return normalized;
  if (action.ladderId != null) return "ai_generated";
  return "unknown";
}

/**
 * Analytics-relevant fields of a task, snapshotted BEFORE a mutation
 * (delete/complete) rewrites or removes the row — the event fires after,
 * when the cache row is already gone.
 */
export type TaskAnalyticsContext = {
  actionType: string | null;
  source: string | null;
  weekNumber: number | null;
  stepId: string | null;
  previousStatus: string | null;
  previousNotes: string | null;
  completedBeforeDelete: boolean;
};

/**
 * Accepts any cached task shape (getCurrentWeek rows carry `taskType`,
 * getKanban rows only `actionType`) and never throws on partial rows.
 */
export function buildTaskAnalyticsContext(
  task: unknown,
): TaskAnalyticsContext | null {
  if (!task || typeof task !== "object") return null;
  const record = task as Record<string, unknown>;
  return {
    actionType:
      typeof record.actionType === "string"
        ? record.actionType
        : typeof record.taskType === "string"
          ? record.taskType
          : null,
    source: readTaskAnalyticsSource(task),
    weekNumber: typeof record.weekNumber === "number" ? record.weekNumber : null,
    stepId:
      typeof record.stepId === "string" && record.stepId ? record.stepId : null,
    previousStatus: typeof record.status === "string" ? record.status : null,
    previousNotes:
      typeof record.completionNotes === "string" ? record.completionNotes : null,
    completedBeforeDelete: record.status === "completed",
  };
}
